import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ensureLeadsReportSession,
  loadLeadsReportSection,
  loadLeadsReportSnapshot,
  resetLeadsReportSession,
} from './api';
import {
  buildDashboardModel,
  buildEmbeddedSupplementalSections,
  buildPriorityDashboardModel,
  buildSearchText,
  buildSectionCounts,
  buildSectionFromCardsResponse,
  getActionState,
  hasPriorityReport,
  normalizeSectionKey,
} from './model';
import SafeMarkdown from './SafeMarkdown';
import {
  formatPresentationCopy,
  getInitialPresentationLanguage,
  getPresentationCopy,
  localizedPresentationText,
  persistPresentationLanguage,
} from './presentation';
import './LeadsReportDashboard.css';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const BUILD_RESOURCE_STATES = new Set(['building', 'failed', 'cancelled']);
const DEFAULT_BUILD_POLL_DELAY_MS = 3000;
const MAX_BUILD_POLL_DELAY_MS = 60000;
const MAX_NETWORK_RETRY_DELAY_MS = 30000;

const SUPPLEMENTAL_SECTIONS = [
  { key: 'watchlist', kind: 'watchlist', title: 'Watchlist' },
  { key: 'on_hold', kind: 'on_hold_threads', section_key: 'on_hold_threads', title: 'On hold' },
];

const SECTION_DROPDOWN_ORDER = ['hot', 'getting_colder', 'watchlist', 'on_hold_threads', 'on_hold'];

function LeadsReportDashboard() {
  const { uuid } = useParams();
  const [snapshotResponse, setSnapshotResponse] = useState(null);
  const [buildResource, setBuildResource] = useState(null);
  const [connectionState, setConnectionState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bucketFilter, setBucketFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('');
  const [contactFilter, setContactFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [search, setSearch] = useState('');
  const [supplementalSections, setSupplementalSections] = useState([]);
  const [supplementalLoading, setSupplementalLoading] = useState(false);
  const [supplementalErrors, setSupplementalErrors] = useState({});
  const [dashboardMode, setDashboardMode] = useState('legacy');
  const [presentationLanguage, setPresentationLanguage] = useState(getInitialPresentationLanguage);
  const copy = useMemo(() => getPresentationCopy(presentationLanguage), [presentationLanguage]);

  useEffect(() => {
    const restoreRobots = setRouteMeta('robots', 'noindex,nofollow,noarchive');
    const restoreReferrer = setRouteMeta('referrer', 'no-referrer');
    return () => {
      restoreRobots();
      restoreReferrer();
    };
  }, []);

  useEffect(() => {
    if (!UUID_PATTERN.test(uuid || '')) {
      return undefined;
    }

    let cancelled = false;
    const renew = () => {
      if (!cancelled) {
        ensureLeadsReportSession(uuid).catch(() => {});
      }
    };
    const interval = window.setInterval(renew, 30000);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        renew();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      resetLeadsReportSession();
    };
  }, [uuid]);
  const [topbarVisible, setTopbarVisible] = useState(true);
  const [topbarHeight, setTopbarHeight] = useState(0);
  const topbarRef = useRef(null);

  useLayoutEffect(() => {
    const topbar = topbarRef.current;

    if (!topbar) {
      return undefined;
    }

    const updateTopbarHeight = () => setTopbarHeight(topbar.getBoundingClientRect().height);
    updateTopbarHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateTopbarHeight);
      return () => window.removeEventListener('resize', updateTopbarHeight);
    }

    const resizeObserver = new ResizeObserver(updateTopbarHeight);
    resizeObserver.observe(topbar);
    return () => resizeObserver.disconnect();
  }, [dashboardMode, error, loading, presentationLanguage, topbarVisible]);

  useEffect(() => {
    let cancelled = false;
    let requestInFlight = false;
    let pollTimer = null;
    let requestController = null;
    let networkFailureCount = 0;
    let monitoringComplete = false;

    if (!UUID_PATTERN.test(uuid || '')) {
      setSnapshotResponse(null);
      setBuildResource(null);
      setConnectionState(null);
      setError({ status: 400, message: 'Invalid dashboard link.' });
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setError(null);
    setBuildResource(null);
    setConnectionState(null);
    setSnapshotResponse(null);
    setSupplementalSections([]);
    setSupplementalErrors({});
    setSupplementalLoading(false);
    setDashboardMode('legacy');

    const clearPollTimer = () => {
      if (pollTimer !== null) {
        window.clearTimeout(pollTimer);
        pollTimer = null;
      }
    };

    const browserIsOffline = () => (
      typeof navigator !== 'undefined' && navigator.onLine === false
    );

    const browserIsHidden = () => (
      typeof document !== 'undefined' && document.visibilityState === 'hidden'
    );

    const schedulePoll = (delayMs) => {
      clearPollTimer();
      if (cancelled || browserIsOffline() || browserIsHidden()) {
        return;
      }
      pollTimer = window.setTimeout(() => {
        pollTimer = null;
        loadResource();
      }, delayMs);
    };

    const loadResource = async () => {
      if (cancelled || requestInFlight || browserIsHidden()) {
        return;
      }
      if (browserIsOffline()) {
        setLoading(false);
        setConnectionState('offline');
        return;
      }

      clearPollTimer();
      requestInFlight = true;
      requestController = typeof AbortController === 'undefined' ? null : new AbortController();

      try {
        const data = await loadLeadsReportSnapshot(uuid, {
          signal: requestController?.signal,
        });
        if (cancelled) {
          return;
        }

        networkFailureCount = 0;
        setConnectionState(null);
        setError(null);
        setLoading(false);

        if (isBuildResourceEnvelope(data)) {
          setSnapshotResponse(null);
          setBuildResource(data);
          if (data.resource_state === 'building') {
            schedulePoll(buildPollDelayMs(data.poll_after_seconds));
          } else {
            monitoringComplete = true;
          }
          return;
        }

        if (data?.resource_state && data.resource_state !== 'ready') {
          monitoringComplete = true;
          setBuildResource(null);
          setError({
            status: 502,
            message: 'The dashboard service returned an unsupported response.',
          });
          return;
        }

        setBuildResource(null);
        setSnapshotResponse(data);
        monitoringComplete = true;
      } catch (requestError) {
        if (cancelled || requestError?.name === 'AbortError') {
          return;
        }

        setLoading(false);
        if (isRetryableDashboardRequestError(requestError)) {
          networkFailureCount += 1;
          setConnectionState(browserIsOffline() ? 'offline' : 'reconnecting');
          schedulePoll(networkRetryDelayMs(networkFailureCount));
        } else {
          monitoringComplete = true;
          setBuildResource(null);
          setConnectionState(null);
          setLoading(false);
          setError({
            status: requestError.status,
            message: requestError.payload?.error?.message
              || requestError.payload?.message
              || 'Unable to load the leads report dashboard.',
          });
        }
      } finally {
        requestInFlight = false;
        requestController = null;
      }
    };

    const resumePolling = () => {
      if (cancelled || monitoringComplete || browserIsOffline() || browserIsHidden()) {
        return;
      }
      clearPollTimer();
      loadResource();
    };

    const onVisibilityChange = () => {
      if (browserIsHidden()) {
        clearPollTimer();
      } else {
        resumePolling();
      }
    };

    const onOffline = () => {
      if (monitoringComplete) {
        return;
      }
      clearPollTimer();
      setLoading(false);
      setConnectionState('offline');
    };

    const onOnline = () => {
      if (monitoringComplete) {
        return;
      }
      setConnectionState('reconnecting');
      resumePolling();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    loadResource();

    return () => {
      cancelled = true;
      clearPollTimer();
      requestController?.abort();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, [uuid]);


  useEffect(() => {
    let cancelled = false;

    if (!snapshotResponse || !UUID_PATTERN.test(uuid || '')) {
      return () => {
        cancelled = true;
      };
    }

    setSupplementalLoading(true);
    setSupplementalErrors({});
    const embeddedSections = buildEmbeddedSupplementalSections(snapshotResponse);
    const sectionsToLoad = SUPPLEMENTAL_SECTIONS.filter((sectionConfig) => (
      !hasSection(embeddedSections, sectionConfig.key)
    ));

    if (sectionsToLoad.length === 0) {
      setSupplementalSections([]);
      setSupplementalLoading(false);
      return () => {
        cancelled = true;
      };
    }

    Promise.allSettled(sectionsToLoad.map((sectionConfig) => (
      loadLeadsReportSection(uuid, sectionConfig.key)
        .then((data) => buildSectionFromCardsResponse(data, sectionConfig))
        .then((section) => ({ ...sectionConfig, section }))
    )))
      .then((results) => {
        if (cancelled) {
          return;
        }

        const nextSections = [];
        const nextErrors = {};

        results.forEach((result, index) => {
          const sectionConfig = sectionsToLoad[index];

          if (result.status === 'fulfilled' && result.value?.section) {
            nextSections.push(result.value.section);
          } else {
            nextErrors[sectionConfig.key] = result.reason?.payload?.message || 'Unable to load this section.';
          }
        });

        setSupplementalSections(nextSections);
        setSupplementalErrors(nextErrors);
      })
      .finally(() => {
        if (!cancelled) {
          setSupplementalLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [snapshotResponse, uuid]);

  const legacyDashboard = useMemo(() => buildDashboardModel(snapshotResponse || {}), [snapshotResponse]);
  const legacyDashboardSections = useMemo(
    () => applySectionPrecedence(mergeSections(legacyDashboard.sections, supplementalSections)),
    [legacyDashboard.sections, supplementalSections],
  );
  const priorityAvailable = useMemo(() => hasPriorityReport(snapshotResponse || {}), [snapshotResponse]);
  const priorityDashboard = useMemo(
    () => buildPriorityDashboardModel(snapshotResponse || {}, legacyDashboardSections),
    [legacyDashboardSections, snapshotResponse],
  );
  const dashboard = dashboardMode === 'priority' && priorityAvailable ? priorityDashboard : legacyDashboard;
  const dashboardSections = useMemo(
    () => applySectionPrecedence(mergeSections(dashboard.sections, supplementalSections)),
    [dashboard.sections, supplementalSections],
  );
  const dashboardCards = useMemo(() => dashboardSections.flatMap((section) => section.cards || []), [dashboardSections]);
  const displayCounts = useMemo(() => ({
    ...dashboard.counts,
    ...buildSectionCounts(dashboardSections),
  }), [dashboard.counts, dashboardSections]);

  const bucketOptions = useMemo(() => orderSectionOptions(uniqueValues(dashboardCards.map((card) => card.bucket))), [dashboardCards]);
  const companyOptions = useMemo(() => uniqueValues(dashboardCards.map((card) => card.organization)), [dashboardCards]);
  const contactOptions = useMemo(() => uniqueValues(dashboardCards.map((card) => card.contact)), [dashboardCards]);
  const productOptions = useMemo(() => uniqueValues(dashboardCards.map((card) => card.product)), [dashboardCards]);

  const filteredSections = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const normalizedCompany = companyFilter.trim().toLowerCase();
    const normalizedContact = contactFilter.trim().toLowerCase();
    const normalizedProduct = productFilter.trim().toLowerCase();

    return dashboardSections.map((section) => {
      const sectionCards = Array.isArray(section.cards) ? section.cards : [];
      const cards = sectionCards
        .map((card) => ({
          ...card,
          normalized: card,
        }))
        .filter(({ normalized }) => {
          if (!normalized) {
            return false;
          }
          const matchesBucket = bucketFilter === 'all' || sectionDropdownKey(normalized.bucket) === sectionDropdownKey(bucketFilter);
          const matchesCompany = matchesAutocompleteFilter(normalized.organization, normalizedCompany);
          const matchesContact = matchesAutocompleteFilter(normalized.contact, normalizedContact);
          const matchesProduct = matchesAutocompleteFilter(normalized.product, normalizedProduct);
          const matchesSearch = !normalizedSearch || buildSearchText(normalized).includes(normalizedSearch);
          return matchesBucket && matchesCompany && matchesContact && matchesProduct && matchesSearch;
        });

      return {
        ...section,
        cards,
      };
    }).filter((section) => section.cards.length > 0);
  }, [bucketFilter, companyFilter, contactFilter, dashboardCards, dashboardSections, productFilter, search]);

  const visibleCardCount = filteredSections.reduce((total, section) => total + section.cards.length, 0);

  const applySectionFilter = (sectionKey) => {
    const matchingBucket = bucketOptions.find((bucket) => sectionDropdownKey(bucket) === sectionDropdownKey(sectionKey));
    setBucketFilter(matchingBucket || sectionKey);
  };

  const clearDashboardFilters = () => {
    setBucketFilter('all');
    setCompanyFilter('');
    setContactFilter('');
    setProductFilter('');
    setSearch('');
  };

  const changePresentationLanguage = (language) => {
    setPresentationLanguage(language);
    persistPresentationLanguage(language);
  };

  const dashboardTitle = localizedPresentationText(
    dashboard.presentation,
    presentationLanguage,
    'title',
    dashboard.title,
  );
  const executiveSummary = localizedPresentationText(
    dashboard.presentation,
    presentationLanguage,
    'summary_markdown',
    dashboard.summaryMarkdown,
  );

  if (buildResource) {
    return (
      <DashboardBuildState
        build={buildResource}
        connectionState={connectionState}
        copy={copy}
      />
    );
  }

  if (connectionState) {
    return (
      <DashboardState
        eyebrow={copy.reportEyebrow}
        title={copy.reconnectingTitle}
        message={connectionState === 'offline' ? copy.offlineMessage : copy.reconnectingMessage}
      />
    );
  }

  if (loading) {
    return <DashboardState eyebrow={copy.reportEyebrow} title={copy.loadingTitle} message={copy.loadingMessage} />;
  }

  if (error) {
    return <DashboardState eyebrow={copy.reportEyebrow} title={getErrorTitle(error.status, copy)} message={error.message} />;
  }

  return (
    <main className="leads-report-dashboard" lang={copy.localeTag}>
      <div
        id="leads-report-topbar"
        className="leads-report-topbar-spacer"
        style={topbarVisible && topbarHeight ? { height: `${topbarHeight}px` } : undefined}
      >
        {topbarVisible && <div className="leads-report-topbar" ref={topbarRef}>
          <section className="leads-report-hero">
            <div>
              <p className="eyebrow">{copy.reportEyebrow}</p>
              <h1 title={dashboardTitle}>{dashboardTitle}</h1>
              <p className="subtitle">
                {formatDisplayDate(dashboard.date, copy)}
                {formatSnapshotGeneratedAt(dashboard.generatedAt, dashboard.timezone, copy)}
              </p>
            </div>
            <div className="leads-report-hero-actions">
              <div className="presentation-language-switch" role="group" aria-label={copy.languageLabel}>
                {['es', 'en'].map((language) => (
                  <button
                    key={language}
                    type="button"
                    aria-pressed={presentationLanguage === language}
                    onClick={() => changePresentationLanguage(language)}
                  >
                    {language.toUpperCase()}
                  </button>
                ))}
              </div>
              <div className="snapshot-control-row">
                <div className="snapshot-pill">{copy.snapshot}</div>
                <div className="dashboard-view-switch" role="group" aria-label={copy.viewLabel}>
                  <button
                    type="button"
                    aria-pressed={dashboardMode === 'legacy'}
                    onClick={() => setDashboardMode('legacy')}
                  >
                    {copy.generalView}
                  </button>
                  {priorityAvailable && (
                    <button
                      type="button"
                      aria-pressed={dashboardMode === 'priority'}
                      onClick={() => setDashboardMode('priority')}
                    >
                      {copy.priorityOrder}
                    </button>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="topbar-toggle-button"
                aria-controls="leads-report-topbar"
                aria-expanded="true"
                onClick={() => setTopbarVisible(false)}
              >
                {copy.hideTopbar}
              </button>
            </div>
          </section>

          <section className="leads-report-kpis" aria-label={copy.dashboardCounters}>
            <KpiCard
              label={copy.openThreads}
              value={displayCounts.open_threads}
              onClick={clearDashboardFilters}
              active={bucketFilter === 'all' && companyFilter.trim() === '' && contactFilter.trim() === '' && productFilter.trim() === '' && search.trim() === ''}
              ariaLabel={copy.showAllOpenThreads}
            />
            <KpiCard
              label={copy.hot}
              value={displayCounts.hot}
              tone="hot"
              onClick={() => applySectionFilter('hot')}
              active={sectionDropdownKey(bucketFilter) === 'hot'}
              ariaLabel={copy.filterHot}
            />
            <KpiCard
              label={copy.watchlist}
              value={displayCounts.watchlist}
              tone="watch"
              loading={supplementalLoading && !hasSection(dashboardSections, 'watchlist')}
              onClick={() => applySectionFilter('watchlist')}
              active={sectionDropdownKey(bucketFilter) === 'watchlist'}
              ariaLabel={copy.filterWatchlist}
            />
            <KpiCard
              label={copy.gettingColder}
              value={displayCounts.getting_colder}
              tone="cold"
              onClick={() => applySectionFilter('getting_colder')}
              active={sectionDropdownKey(bucketFilter) === 'getting_colder'}
              ariaLabel={copy.filterGettingColder}
            />
            <KpiCard
              label={copy.onHold}
              value={displayCounts.on_hold_threads}
              loading={supplementalLoading && !hasSection(dashboardSections, 'on_hold_threads')}
              onClick={() => applySectionFilter('on_hold_threads')}
              active={sectionDropdownKey(bucketFilter) === 'on_hold_threads'}
              ariaLabel={copy.filterOnHold}
            />
            {dashboardMode === 'priority' && priorityAvailable && (
              <KpiCard
                label={copy.incompletePriority}
                value={displayCounts.total_incomplete || 0}
                ariaLabel={copy.incompletePriorityHelp}
              />
            )}
          </section>

          <section className="leads-report-controls" aria-label={copy.dashboardFilters}>
            <label>
              {copy.company}
              <input
                type="search"
                list="leads-report-company-options"
                value={companyFilter}
                placeholder={copy.allCompanies}
                autoComplete="off"
                onChange={(event) => setCompanyFilter(event.target.value)}
              />
              <datalist id="leads-report-company-options">
                {companyOptions.map((company) => <option key={company} value={company} />)}
              </datalist>
            </label>
            <label>
              {copy.contact}
              <input
                type="search"
                list="leads-report-contact-options"
                value={contactFilter}
                placeholder={copy.allContacts}
                autoComplete="off"
                onChange={(event) => setContactFilter(event.target.value)}
              />
              <datalist id="leads-report-contact-options">
                {contactOptions.map((contact) => <option key={contact} value={contact} />)}
              </datalist>
            </label>
            <label>
              {copy.product}
              <input
                type="search"
                list="leads-report-product-options"
                value={productFilter}
                placeholder={copy.allProducts}
                autoComplete="off"
                onChange={(event) => setProductFilter(event.target.value)}
              />
              <datalist id="leads-report-product-options">
                {productOptions.map((product) => <option key={product} value={product} />)}
              </datalist>
            </label>
            <label className="search-control">
              {copy.search}
              <input
                type="search"
                value={search}
                placeholder={copy.searchPlaceholder}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <div className="result-count">
              {formatPresentationCopy(visibleCardCount === 1 ? copy.visibleLead : copy.visibleLeads, { count: visibleCardCount })}
            </div>
          </section>
        </div>}
      </div>

      {!topbarVisible && (
        <button
          type="button"
          className="topbar-toggle-button show-topbar-button"
          aria-controls="leads-report-topbar"
          aria-expanded="false"
          onClick={() => setTopbarVisible(true)}
        >
          {copy.showTopbar}
        </button>
      )}

      {executiveSummary && (
        <ExecutiveSummary markdown={executiveSummary} copy={copy} />
      )}

      <SupplementalStatus loading={supplementalLoading} errors={supplementalErrors} copy={copy} />

      {filteredSections.length === 0 ? (
        <DashboardState eyebrow={copy.reportEyebrow} title={copy.noMatchesTitle} message={copy.noMatchesMessage} compact />
      ) : (
        filteredSections.map((section) => (
          <LeadSection
            key={section.kind || section.title}
            section={section}
            language={presentationLanguage}
            copy={copy}
          />
        ))
      )}
    </main>
  );
}

function ExecutiveSummary({ markdown, copy }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <section className="executive-summary-card" aria-label={copy.executiveSummary}>
      <button
        type="button"
        className="executive-summary-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
      >
        <span>{copy.executiveSummary}</span>
        <span aria-hidden="true">{expanded ? '−' : '+'}</span>
      </button>
      {expanded && <SafeMarkdown markdown={markdown} />}
    </section>
  );
}

function LeadSection({ section, language, copy }) {
  const title = localizedSectionTitle(section, copy);
  return (
    <section className="lead-section">
      <div className="section-heading">
        <div>
          <p className="eyebrow">{copy.section}</p>
          <h2>{title || copy.leads}</h2>
        </div>
        <span>
          {formatPresentationCopy(section.cards.length === 1 ? copy.leadCount : copy.leadsCount, { count: section.cards.length })}
        </span>
      </div>
      <div className="lead-card-grid">
        {section.cards.map(({ normalized }) => (
          <LeadCard key={normalized.id} card={normalized} language={language} copy={copy} />
        ))}
      </div>
    </section>
  );
}

function LeadCard({ card, language, copy }) {
  const actionState = getActionState(card, copy);
  const actionLabel = localizedPresentationText(card.presentation, language, 'label', actionState.label);
  const generatedSummary = localizedPresentationText(card.presentation, language, 'summary', card.summary);
  const recommendedAction = localizedPresentationText(
    card.presentation,
    language,
    'recommended_action',
    card.recommendedAction === 'Review manually' ? copy.reviewManually : card.recommendedAction,
  );
  const fieldAlert = actionState.fieldAlert;
  const leadHealth = getLeadHealthState(card.leadHealth, copy);
  const [copyMessage, setCopyMessage] = useState('');

  const copyLeadDetails = () => {
    copyTextToClipboard(formatLeadDetailsForClipboard(card, copy)).then((copied) => {
      setCopyMessage(copied ? copy.leadCopied : copy.unableToCopy);
      window.setTimeout(() => setCopyMessage(''), 1800);
    });
  };

  const organization = card.organization === 'Unknown organization' ? copy.unknownOrganization : card.organization;
  const contact = card.contact === 'Unknown contact' ? copy.unknownContact : card.contact;
  const product = card.product === 'Unknown product' ? copy.unknownProduct : card.product;

  return (
    <article className={`lead-card ${card.bucket ? slugClass(card.bucket) : ''}`}>
      <button
        type="button"
        className="copy-lead-details-button"
        onClick={copyLeadDetails}
        aria-label={formatPresentationCopy(copy.copyLeadAria, { organization: organization || copy.leads })}
        title={copy.copyLeadTitle}
      >
        ⧉
      </button>
      {copyMessage && (
        <span className="copy-lead-details-message" role="status" aria-live="polite">
          {copyMessage}
        </span>
      )}
      <div className="lead-card-header">
        <div>
          <h3>{organization}</h3>
          <p>{contact}</p>
        </div>
        <div className="lead-card-badges">
          {card.guidanceConsidered && (
            <span className="lead-guidance-badge" aria-label={copy.guidanceConsidered}>
              <span aria-hidden="true">✓</span>
              <span>{copy.guidanceConsidered}</span>
            </span>
          )}
          {card.tag && <span className="lead-generated-tag">{card.tag}</span>}
          {card.priority?.combinationCode && <PriorityBadge priority={card.priority} copy={copy} />}
          <div className={`lead-health-badge health-${leadHealth.status}`} tabIndex="0" aria-label={leadHealth.ariaLabel}>
            <span className="lead-health-dot" aria-hidden="true" />
            <span>{leadHealth.label}</span>
            <span className="lead-health-tooltip" role="tooltip">
              <strong>{copy.leadHealth}: {leadHealth.label}{leadHealth.score !== null ? ` · ${leadHealth.score}` : ''}</strong>
              {leadHealth.reasons.length > 0 && (
                <span>{leadHealth.reasons.join(' · ')}</span>
              )}
              <span>{copy.channel}: {formatLabel(actionState.channel)}</span>
            </span>
          </div>
        </div>
      </div>

      <div className="lead-summary-stack">
        <div className="lead-product">{product}</div>

        {generatedSummary && <p className="lead-generated-summary">{generatedSummary}</p>}

        {(card.stage || card.status || card.businessStatus) && (
          <div className="lead-stage-summary">
            <div className="lead-stage-title-row">
              {card.stage && <strong>{formatLabel(card.stage)}</strong>}
              {(card.status || card.businessStatus) && (
                <button
                  type="button"
                  className="stage-info-trigger"
                  aria-label={`${copy.statusDetails}: ${[formatLabel(card.status), formatLabel(card.businessStatus)].filter(Boolean).join(' · ')}`}
                >
                  i
                  <span className="stage-info-tooltip" role="tooltip">
                    {[formatLabel(card.status), formatLabel(card.businessStatus)].filter(Boolean).join(' · ')}
                  </span>
                </button>
              )}
            </div>
          </div>
        )}

        {(card.latestActivityDate || card.nextScheduledMeetingDate) && (
          <div className="lead-date-summary">
            {card.latestActivityDate && <span>{copy.active} {formatShortDate(card.latestActivityDate, copy)}</span>}
            {card.nextScheduledMeetingDate && <span>{copy.meeting} {formatShortDate(card.nextScheduledMeetingDate, copy)}</span>}
          </div>
        )}
      </div>

      <div className="recommended-action">
        <strong>{copy.nextAction.toUpperCase()}</strong>
        <p>{recommendedAction}</p>
      </div>

      {(card.latestMeetingSummary || card.latestNoteText) && (
        <details className="lead-context latest-update">
          <summary>{copy.latestUpdate.toUpperCase()}</summary>
          <div>
            {card.latestNoteText && card.latestNoteText !== card.latestMeetingSummary && (
              <p><strong>{copy.latestNote}:</strong> {card.latestNoteText}</p>
            )}
            {card.latestMeetingSummary && (
              <div className="summary-block">
                <strong>{copy.summary}:</strong>
                {formatSummaryEntries(card.latestMeetingSummary).map((entry, index) => (
                  <p key={`${entry}-${index}`}>{entry}</p>
                ))}
              </div>
            )}
          </div>
        </details>
      )}

      {actionState.messageText && (
        <details className="message-preview">
          <summary>{copy.messagePreview}</summary>
          <p>{actionState.messageText}</p>
        </details>
      )}

      {fieldAlert && <LeadFieldAlert alert={fieldAlert} />}

      {actionState.disabled ? (
        <button className="action-button disabled" type="button" disabled>{actionLabel}</button>
      ) : (
        <a className="action-button" href={actionState.url} target="_blank" rel="noopener noreferrer">{actionLabel}</a>
      )}
    </article>
  );
}

function PriorityBadge({ priority, copy }) {
  const details = [
    `${copy.revenuePotential}: ${priority.revenueStrategicPotentialCode || copy.unknown}`,
    `${copy.interest}: ${priority.interestLevelCode || copy.unknown}`,
    `${copy.valuesAlignment}: ${priority.valuesAlignmentCode || copy.unknown}`,
  ];
  const ariaLabel = [`${copy.priority} ${priority.combinationCode}`, ...details];

  if (priority.rank !== undefined && priority.rank !== null) {
    ariaLabel.push(`${copy.rank} ${priority.rank}`);
  }

  return (
    <div className="priority-code-badge" tabIndex="0" aria-label={ariaLabel.join('. ')}>
      <span>{copy.priority} {priority.combinationCode}</span>
      <span className="priority-code-tooltip" role="tooltip">
        <strong>{copy.priority} {priority.combinationCode}</strong>
        {details.map((detail) => <span key={detail}>{detail}</span>)}
        {priority.rank !== undefined && priority.rank !== null && <span>{copy.rank}: {priority.rank}</span>}
      </span>
    </div>
  );
}

function LeadFieldAlert({ alert }) {
  if (!alert) {
    return null;
  }

  return (
    <div className="lead-field-alert" role="note" aria-label={alert.message}>
      <span className="lead-field-alert-warning-icon" aria-hidden="true">⚠</span>
      <div className="lead-field-alert-content">
        <span className="lead-field-alert-message">{alert.message}</span>
        <button
          type="button"
          className="lead-field-alert-info"
          aria-label={alert.tooltip}
          title={alert.tooltip}
        >
          i
          <span className="lead-field-alert-tooltip" role="tooltip">{alert.tooltip}</span>
        </button>
      </div>
    </div>
  );
}

function mergeSections(baseSections = [], extraSections = []) {
  const merged = [...baseSections];

  extraSections.forEach((section) => {
    if (!section) {
      return;
    }

    const index = merged.findIndex((candidate) => sameSection(candidate, section));
    if (index >= 0) {
      merged[index] = section;
    } else {
      merged.push(section);
    }
  });

  return merged;
}

function applySectionPrecedence(sections = []) {
  const rankedSections = sections.map((section, sectionIndex) => ({
    section,
    sectionIndex,
    rank: sectionPrecedence(section),
  }));
  const bestRankByLead = new Map();

  rankedSections.forEach(({ section, rank }) => {
    (section.cards || []).forEach((card) => {
      const key = dashboardCardIdentity(card);
      if (!key) {
        return;
      }
      bestRankByLead.set(key, Math.min(bestRankByLead.get(key) ?? rank, rank));
    });
  });

  const emitted = new Set();
  return rankedSections.map(({ section, rank }) => ({
    ...section,
    cards: (section.cards || []).filter((card) => {
      const key = dashboardCardIdentity(card);
      if (!key) {
        return true;
      }
      if (bestRankByLead.get(key) !== rank || emitted.has(key)) {
        return false;
      }
      emitted.add(key);
      return true;
    }),
  }));
}

function sectionPrecedence(section) {
  const key = normalizeSectionKey(section);
  if (key === 'hot' || key === 'getting_colder') {
    return 0;
  }
  if (key === 'on_hold_threads' || key === 'on_hold') {
    return 1;
  }
  if (key === 'watchlist') {
    return 2;
  }
  return 3;
}

function dashboardCardIdentity(card = {}) {
  const source = card.sourceCard || card.normalized?.sourceCard || card.normalized || card;
  const leadKey = String(
    card.leadKey
      || card.snapshotCard?.lead_key
      || source.lead_key
      || '',
  ).trim().toLowerCase();
  if (leadKey) {
    return leadKey;
  }
  const referenceType = String(source.lead_reference_type || 'lead').trim().toLowerCase();
  const referenceId = String(source.lead_reference_id || '').trim().toLowerCase();
  const product = String(source.product_name || source.product || '').trim().toLowerCase().replace(/\s+/g, ' ');
  return referenceId ? `${referenceType}:${referenceId}:${product}` : null;
}

function setRouteMeta(name, content) {
  let element = document.head.querySelector(`meta[name="${name}"]`);
  const existed = Boolean(element);
  const previousContent = element?.getAttribute('content');
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('name', name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);

  return () => {
    if (!existed) {
      element.remove();
    } else if (previousContent === null) {
      element.removeAttribute('content');
    } else {
      element.setAttribute('content', previousContent);
    }
  };
}

function sameSection(left = {}, right = {}) {
  const leftKeys = [left.section_key, left.kind, left.title].filter(Boolean).map(slugValue);
  const rightKeys = [right.section_key, right.kind, right.title].filter(Boolean).map(slugValue);
  return leftKeys.some((key) => rightKeys.includes(key));
}

function hasSection(sections, key) {
  const normalizedKey = slugValue(key);
  return sections.some((section) => [section.section_key, section.kind, section.title].filter(Boolean).map(slugValue).includes(normalizedKey));
}

function sectionLabel(key, copy) {
  const section = SUPPLEMENTAL_SECTIONS.find((candidate) => candidate.key === key);
  const normalizedKey = normalizeSectionKey(section || key);
  return copy?.sectionLabels?.[normalizedKey] || section?.title || formatLabel(key) || copy?.section || 'Section';
}

function localizedSectionTitle(section, copy) {
  const normalizedKey = normalizeSectionKey(section);
  return copy.sectionLabels?.[normalizedKey] || section.title || section.kind || copy.section;
}

function orderSectionOptions(options = []) {
  return [...options].sort((left, right) => {
    const leftIndex = sectionDropdownOrderIndex(left);
    const rightIndex = sectionDropdownOrderIndex(right);

    if (leftIndex !== rightIndex) {
      return leftIndex - rightIndex;
    }

    return String(left || '').localeCompare(String(right || ''));
  });
}

function sectionDropdownOrderIndex(value) {
  const dropdownKey = sectionDropdownKey(value);
  const index = SECTION_DROPDOWN_ORDER.indexOf(dropdownKey);
  return index >= 0 ? index : SECTION_DROPDOWN_ORDER.length;
}

function sectionDropdownKey(value) {
  return normalizeSectionKey(value);
}

function slugValue(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function getLeadHealthState(leadHealth, copy) {
  const normalizedStatus = normalizeHealthStatus(leadHealth?.status);
  const label = normalizedStatus === 'unknown'
    ? leadHealth?.label || healthLabel(normalizedStatus, copy)
    : healthLabel(normalizedStatus, copy);
  const score = Number.isFinite(Number(leadHealth?.score)) ? Number(leadHealth.score) : null;
  const reasons = Array.isArray(leadHealth?.reasons)
    ? leadHealth.reasons
      .map((reason) => (typeof reason === 'string' ? reason : reason?.label))
      .filter(Boolean)
    : [];
  const ariaParts = [`${copy.leadHealth}: ${label}`];

  if (score !== null) {
    ariaParts.push(`${copy.score} ${score}`);
  }

  if (reasons.length > 0) {
    ariaParts.push(reasons.join('. '));
  }

  return {
    status: normalizedStatus,
    label,
    score,
    reasons,
    ariaLabel: ariaParts.join('. '),
  };
}

function normalizeHealthStatus(status) {
  const value = String(status || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');

  if (['healthy', 'watch', 'at_risk', 'blocked'].includes(value)) {
    return value;
  }

  return 'unknown';
}

function healthLabel(status, copy) {
  if (status === 'healthy') {
    return copy.healthHealthy;
  }
  if (status === 'watch') {
    return copy.healthWatch;
  }
  if (status === 'at_risk') {
    return copy.healthAtRisk;
  }
  if (status === 'blocked') {
    return copy.healthBlocked;
  }
  return copy.healthUnknown;
}

function SupplementalStatus({ loading, errors, copy }) {
  const errorEntries = Object.entries(errors || {});

  if (!loading && errorEntries.length === 0) {
    return null;
  }

  return (
    <section className="supplemental-status" aria-live="polite">
      {loading && <span>{copy.loadingSupplemental}</span>}
      {errorEntries.map(([key, message]) => (
        <span key={key}>
          {formatPresentationCopy(copy.supplementalError, { section: sectionLabel(key, copy), message })}
        </span>
      ))}
    </section>
  );
}

function KpiCard({ label, value, tone, loading, onClick, active, ariaLabel }) {
  const className = [
    'kpi-card',
    tone ? `tone-${tone}` : '',
    onClick ? 'is-clickable' : '',
    active ? 'is-active' : '',
  ].filter(Boolean).join(' ');
  const content = (
    <>
      <span>{label}</span>
      <strong>{loading ? '...' : value ?? 0}</strong>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={className}
        onClick={onClick}
        aria-pressed={Boolean(active)}
        aria-label={ariaLabel || label}
      >
        {content}
      </button>
    );
  }

  return <div className={className} aria-label={ariaLabel || label}>{content}</div>;
}

function DashboardState({ eyebrow, title, message, compact }) {
  return (
    <main className={`leads-report-dashboard state ${compact ? 'compact' : ''}`}>
      <div className="dashboard-state-card">
        <p className="eyebrow">{eyebrow || 'AI Leads Report'}</p>
        <h1>{title}</h1>
        <p>{message}</p>
      </div>
    </main>
  );
}

function DashboardBuildState({ build, connectionState, copy }) {
  const progress = normalizedBuildProgress(build.progress);
  const reviewRequired = build.error?.code === 'dashboard_review_required';
  const stateCopy = {
    building: {
      title: copy.buildingTitle,
      message: copy.buildingMessage,
    },
    failed: {
      title: reviewRequired ? copy.awaitingPublicationTitle : copy.buildFailedTitle,
      message: reviewRequired ? copy.awaitingPublicationMessage : copy.buildFailedMessage,
    },
    cancelled: {
      title: copy.buildCancelledTitle,
      message: copy.buildCancelledMessage,
    },
  }[build.resource_state];
  const stageLabel = {
    SOURCE: copy.buildStageSource,
    GENERATE: copy.buildStageGenerate,
    FINALIZE: copy.buildStageFinalize,
  }[build.stage] || copy.buildStagePreparing;

  return (
    <main className="leads-report-dashboard state" lang={copy.localeTag}>
      <div className="dashboard-state-card dashboard-build-state" aria-live="polite">
        <p className="eyebrow">{copy.reportEyebrow}</p>
        <h1>{stateCopy.title}</h1>
        <p>{stateCopy.message}</p>
        <div className="dashboard-build-progress" role="status">
          <div className="dashboard-build-progress-heading">
            <span>{stageLabel}</span>
            <strong>{formatPresentationCopy(copy.buildPercent, { percent: progress.percent_complete })}</strong>
          </div>
          <progress
            aria-label={copy.buildProgressAria}
            max="100"
            value={progress.percent_complete}
          />
          {progress.total_items > 0 && (
            <p>
              {formatPresentationCopy(copy.buildProgress, {
                completed: progress.completed_items,
                total: progress.total_items,
              })}
            </p>
          )}
        </div>
        {connectionState && (
          <p className="dashboard-build-connection">
            {connectionState === 'offline' ? copy.offlineMessage : copy.reconnectingMessage}
          </p>
        )}
      </div>
    </main>
  );
}

function isBuildResourceEnvelope(value) {
  return Boolean(
    value
    && typeof value === 'object'
    && !Array.isArray(value)
    && BUILD_RESOURCE_STATES.has(value.resource_state),
  );
}

function buildPollDelayMs(pollAfterSeconds) {
  const seconds = Number(pollAfterSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return DEFAULT_BUILD_POLL_DELAY_MS;
  }
  return Math.min(MAX_BUILD_POLL_DELAY_MS, Math.max(1000, seconds * 1000));
}

function networkRetryDelayMs(failureCount) {
  const exponent = Math.max(0, Number(failureCount || 1) - 1);
  return Math.min(MAX_NETWORK_RETRY_DELAY_MS, DEFAULT_BUILD_POLL_DELAY_MS * (2 ** exponent));
}

function isRetryableDashboardRequestError(error) {
  const status = Number(error?.status);
  return !Number.isFinite(status)
    || status === 408
    || status === 425
    || status === 429
    || status >= 500;
}

function normalizedBuildProgress(progress = {}) {
  const totalItems = nonNegativeInteger(progress.total_items);
  const reportedCompletedItems = nonNegativeInteger(progress.completed_items);
  const reportedFailedItems = nonNegativeInteger(progress.failed_items);
  const completedItems = totalItems > 0
    ? Math.min(totalItems, reportedCompletedItems)
    : reportedCompletedItems;
  const failedItems = totalItems > 0
    ? Math.min(Math.max(0, totalItems - completedItems), reportedFailedItems)
    : reportedFailedItems;
  const calculatedPercent = totalItems > 0
    ? Math.floor(((completedItems + failedItems) / totalItems) * 100)
    : 0;
  const reportedPercent = Number(progress.percent_complete);
  const percentComplete = Number.isFinite(reportedPercent)
    ? Math.min(100, Math.max(0, Math.round(reportedPercent)))
    : calculatedPercent;

  return {
    total_items: totalItems,
    completed_items: completedItems,
    failed_items: failedItems,
    percent_complete: percentComplete,
  };
}

function nonNegativeInteger(value) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.floor(number)) : 0;
}

function uniqueValues(values) {
  return [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

function matchesAutocompleteFilter(value, normalizedFilter) {
  return !normalizedFilter || String(value || '').toLowerCase().includes(normalizedFilter);
}

function parseReportDate(value) {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDisplayDate(value, copy) {
  const date = parseReportDate(value);
  if (!date) {
    return value || copy.dateUnavailable;
  }

  return date.toLocaleDateString(copy.localeTag, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatSnapshotGeneratedAt(value, snapshotTimezone, copy) {
  if (!value) {
    return snapshotTimezone ? ` · ${snapshotTimezone}` : '';
  }

  const generatedAt = new Date(value);
  if (Number.isNaN(generatedAt.getTime())) {
    return snapshotTimezone ? ` · ${snapshotTimezone}` : '';
  }

  const timeZone = validTimeZone(snapshotTimezone) ? snapshotTimezone : 'UTC';
  const localTime = generatedAt.toLocaleTimeString(copy.localeTag, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  });

  return ` · ${copy.generated} ${localTime} · ${timeZone}`;
}

function validTimeZone(timeZone) {
  if (!timeZone) {
    return false;
  }

  try {
    Intl.DateTimeFormat('en-US', { timeZone }).format();
    return true;
  } catch (_ignored) {
    return false;
  }
}

function formatShortDate(value, copy) {
  const date = parseReportDate(value);
  if (!date) {
    return value || copy.dateUnavailable;
  }

  return date.toLocaleDateString(copy.localeTag, {
    month: 'short',
    day: 'numeric',
  });
}

function formatLeadDetailsForClipboard(card = {}, copy) {
  return [
    `${copy.company}: ${card.organization || ''}`,
    `${copy.contact}: ${card.contact || ''}`,
    `${copy.product}: ${card.product || ''}`,
  ].join('\n');
}

function copyTextToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text)
      .then(() => true)
      .catch(() => fallbackCopyTextToClipboard(text));
  }

  return Promise.resolve(fallbackCopyTextToClipboard(text));
}

function fallbackCopyTextToClipboard(text) {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.top = '-9999px';
  textArea.style.left = '-9999px';
  document.body.appendChild(textArea);
  textArea.select();

  try {
    return document.execCommand('copy');
  } catch (error) {
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
}

function formatSummaryEntries(value, maxEntries = 3) {
  const text = String(value || '').trim();

  if (!text) {
    return [];
  }

  const entries = text
    .split(/\s*\|\s*(?=\d{4}-\d{2}-\d{2}:)/)
    .map((entry) => entry.trim())
    .filter(Boolean);

  return (entries.length > 0 ? entries : [text]).slice(0, maxEntries);
}

function formatLabel(value) {
  if (!value) {
    return undefined;
  }

  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\w/g, (letter) => letter.toUpperCase());
}

function getErrorTitle(status, copy) {
  if (status === 400) {
    return copy.invalidLinkTitle;
  }
  if (status === 404) {
    return copy.notFoundTitle;
  }
  return copy.unableTitle;
}

function slugClass(value) {
  return `bucket-${String(value).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}

export default LeadsReportDashboard;
